import { useMutation, useQueryClient } from '@tanstack/react-query';

import backendService from '../services/backendService';
import { Game } from '../services/backendService/types';
import { gameCacheKey } from './useGame';
import { newRelicNoticeError } from './useNewRelic';

function useAssignClass(gameId: string | undefined) {
  const queryClient = useQueryClient();
  const cacheKey = [gameCacheKey, gameId];

  const mutation = useMutation({
    mutationFn: (classId: string | null) => {
      if (!gameId) return Promise.reject(new Error('No gameId'));
      return backendService.instance.game.assignClass({ gameId, classId });
    },
    onMutate: async (classId: string | null) => {
      await queryClient.cancelQueries({ queryKey: cacheKey });
      const previousGame = queryClient.getQueryData<Game>(cacheKey);
      queryClient.setQueryData<Game | undefined>(cacheKey, game =>
        game ? { ...game, assignedClassId: classId } : game,
      );
      return { previousGame };
    },
    onError: (err, _classId, context) => {
      newRelicNoticeError(err, 'Error while assigning class to game');
      if (context?.previousGame !== undefined) {
        queryClient.setQueryData(cacheKey, context.previousGame);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: cacheKey });
    },
  });

  return {
    assignClass: mutation.mutate,
    isAssigning: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  };
}

export default useAssignClass;
